/*
 * @providesModule FallbackSkillsThemeProps
 *
 */

import type theme from './themes/cobalt-theme.json';

export type AdditionalThemeProps = {
  fontSemiBold?: {fontFamily: string};
  fontExtraBold?: {fontFamily: string};
};
type FallbackSkillsThemePropsType = {
  sheetStyles: Partial<typeof theme.sheetStyles & AdditionalThemeProps>;
  gradients: {};
  images: {};
};

const FallbackSkillsLightThemeProps: FallbackSkillsThemePropsType = {
  sheetStyles: {
    viewPrimaryColor: {
      backgroundColor: '#ffffff',
    },
    viewSecondaryColor: {
      backgroundColor: '#fbf6f0',
    },
    viewTertiaryColor: {
      backgroundColor: '#fbf6f0',
    },
    viewHighlightColor: {
      backgroundColor: 'rgba(144, 183, 237, .7)',
    },
    statusBarViewBackgroundColor: {
      backgroundColor: '#F9F9F9',
    },
    viewDividerPrimaryColor: {
      backgroundColor: '#2D3345',
    },
    viewDividerColorTwo: {
      backgroundColor: '#bfc0c1',
    },
    cellBackgroundColor: {
      backgroundColor: 'rgba(255,255,255,.95)',
    },
    cellAlternateBackgroundColor: {
      backgroundColor: '#f6f6f6',
    },
    playerActivityIndicatorColor: {
      backgroundColor: '#6AC141',
    },
    progressBarColor: {
      backgroundColor: '#1f8ed4',
    },
    viewCornerRadiusPrimary: {
      borderRadius: 5,
    },
    viewCornerRadiusSecondary: {
      borderRadius: 4,
    },
    viewCornerRadiusTertiary: {
      borderRadius: 2,
    },
    viewBorderWidthPrimary: {
      borderWidth: 3,
    },
    viewBorderWidthSecondary: {
      borderWidth: 1.5,
    },
    viewBorderWidthTertiary: {
      borderWidth: 1,
    },
    viewBoxShadowPrimary: {
      shadowColor: '#000000',
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowRadius: 1,
      shadowOpacity: 0.4,
    },
    fontPrimary: {
      fontFamily: 'HelveticaNeue-Light',
    },
    fontPrimaryItalic: {
      fontFamily: 'HelveticaNeue-LightItalic',
    },
    fontSecondary: {
      fontFamily: 'HelveticaNeue-CondensedBlack',
    },
    fontRegular: {
      fontFamily: 'HelveticaNeue',
    },
    fontMedium: {
      fontFamily: 'HelveticaNeue-Medium',
    },
    fontBold: {
      fontFamily: 'HelveticaNeue-Bold',
    },
    fontBoldItalic: {
      fontFamily: 'HelveticaNeue-BoldItalic',
    },
    fontDigital: {
      fontFamily: 'Digital-7',
    },
    textColorPrimary: {
      color: '#000000',
    },
    textColorSecondary: {
      color: '#113556',
    },
    textColorTertiary: {
      color: '#000000',
    },
    textColorFour: {
      color: '#7a8fac',
      description: 'ie: VS <Username> on Match Stream cells',
    },
    textColorError: {
      color: '#bc2131',
    },
    textColorCash: {
      color: '#3ba038',
    },
    textColorZ: {
      color: '#387aa4',
    },
    textColorTicketz: {
      color: '#FD705D',
    },
    textColorLink: {
      color: '#006699',
    },
    textColorMedals: {
      color: '#317296',
    },
    textColorDepositTile: {
      color: '#FFFFFF',
    },
    textColorMatchSelectPrize: {
      color: '#FFFFFF',
    },
    textColorSideMenu: {
      color: '#FFFFFF',
    },
    textShadowOne: {
      shadowColor: '#000000',
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowRadius: 4,
      shadowOpacity: 0.4,
    },
    textShadowTwo: {
      shadowColor: '#000000',
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowRadius: 1,
      shadowOpacity: 0.3,
    },
    textShadowThree: {
      shadowColor: '#000000',
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowRadius: 1,
      shadowOpacity: 0.2,
    },
    fontSizeExtraLarge: {
      fontSize: 85,
    },
    fontSizeVeryLarge: {
      fontSize: 58,
    },
    fontSizeLarge: {
      fontSize: 42,
    },
    fontSizeMedium: {
      fontSize: 35,
    },
    fontSizeH1: {
      fontSize: 30,
    },
    fontSizeH2: {
      fontSize: 22,
    },
    fontSizeH3: {
      fontSize: 18,
    },
    fontSizeH4: {
      fontSize: 15,
    },
    fontSizeH5: {
      fontSize: 12,
    },
    fontSizeH6: {
      fontSize: 8,
    },
    btnTextColorOne: {
      color: '#ffffff',
    },
    btnTextColorTwo: {
      color: '#ffffff',
    },
    btnTextColorThree: {
      color: '#ffffff',
    },
    featuredMatchBarColor: {
      backgroundColor: '#1f8ed4',
    },
    tabHighlightColor: {
      color: '#1f8ed4',
    },
    checkMarkColor: {
      backgroundColor: '#4fa93f',
    },
    highlightTurnBasedWinColor: {
      color: '#f9eb0d',
    },
    giftBoxHighlightColor: {
      color: '#395b79',
    },
    primaryRibbonTextColor: {
      color: '#ffffff',
    },
    excitingBannerTextColor: {
      color: '#ffffff',
    },
  },
  gradients: {
    prizeViewLiveEventGradient: ['#6ac141', '#35a1a2'],
    prizeViewCashGradient: ['#6ac141', '#36923e'],
    prizeViewZGradient: ['#387aa4', '#387aa4'],
    prizeViewTicketzGradient: ['#f6604d', '#e05745'],
    bracketPrizeViewLiveEventGradient: ['#6ac141', '#35a1a2'],
    bracketPrizeViewCashGradient: ['#6ac141', '#36923e'],
    bracketPrizeViewZGradient: ['#387aa4', '#387aa4'],
    btnGradientOne: ['#A00EA7', '#2333FF'],
    btnGradientTwo: ['#2333FF', '#A00EA7'],
    btnGradientThree: ['#6861ff', '#6861ff'],
    btnGradientStore: ['#4fb031', '#29782f'],
    playButtonGradient: ['#2333FF', '#A00EA7'],
    textTicketzGradient: ['#f87861', '#e1654d'],
    textCashGradient: ['#6ac141', '#3da142'],
    textZGradient: ['#4686ad', '#2f6d98'],
    textNeutralGradient: ['#b6b6b6', '#6c6c6c'],
    overlayEntryContainerGradient: ['rgba(114,127,144,.95)', 'rgba(141,154,168,.95)'],
    backgroundViewGradient: ['#BEBAFF', '#EAC7E7'],
    sideMenuHighlightedGradient: ['#2a6994', '#63afd6'],
    cashIconGradient: ['#691ECD', '#BCAFFF'],
    practiceCurrencyIconGradient: ['#5d7696', '#6da5ce'],
    profileIconGradient: ['#6ea8d2', '#5c7291'],
    progressViewDarkGradient: ['#343443', '#484858'],
    navigationBarGradient: ['#000000', '#262a38'],
    selectedCellBackgroundGradient: ['#0099c7', '#005174'],
    pointsPrizeGradient: ['#4581b1', '#5592c1', '#629dca'],
    tauntImageGradient: ['#4686ad', '#2f6d98'],
    excitingBannerGradient: ['#2e2dff', '#fc0132'],
    excitingHighlightGradient: ['#3e00cd', '#b709dd'],
    primaryRibbonGradient: ['#350c95', '#4b0cdf', '#3d03c7', '#4b0cdf', '#350c95'],
  },
  images: {
    backgroundImage: 'SKillzLightThemeBG.png',
    prizeViewZImage: null,
    prizeViewCashImage: null,
    prizeViewLiveEventImage: null,
    prizeViewTicketzImage: null,
    bracketPrizeViewZImage: null,
    bracketPrizeViewCashImage: null,
    bracketPrizeViewLiveEventImage: null,
    excitingBannerImage: null,
    interstitialBackgroundImage: null,
  },
};

const FallbackSkillsDarkThemeProps: FallbackSkillsThemePropsType = {
  sheetStyles: {
    viewPrimaryColor: {
      backgroundColor: '#0a0d11',
    },
    viewSecondaryColor: {
      // backgroundColor: 'rgba(255,255,255,.85)',
      backgroundColor: '#080808',
    },
    viewTertiaryColor: {
      backgroundColor: 'rgba(255,255,255,.95)',
    },
    viewHighlightColor: {
      backgroundColor: 'rgba(144, 183, 237, .7)',
    },
    statusBarViewBackgroundColor: {
      backgroundColor: '#F9F9F9',
    },
    viewDividerPrimaryColor: {
      backgroundColor: '#2D3345',
    },
    viewDividerColorTwo: {
      backgroundColor: '#bfc0c1',
    },
    cellBackgroundColor: {
      backgroundColor: 'rgba(255,255,255,.95)',
    },
    cellAlternateBackgroundColor: {
      backgroundColor: '#f6f6f6',
    },
    playerActivityIndicatorColor: {
      backgroundColor: '#6AC141',
    },
    progressBarColor: {
      backgroundColor: '#1f8ed4',
    },
    viewCornerRadiusPrimary: {
      borderRadius: 5,
    },
    viewCornerRadiusSecondary: {
      borderRadius: 4,
    },
    viewCornerRadiusTertiary: {
      borderRadius: 2,
    },
    viewBorderWidthPrimary: {
      borderWidth: 3,
    },
    viewBorderWidthSecondary: {
      borderWidth: 1.5,
    },
    viewBorderWidthTertiary: {
      borderWidth: 1,
    },
    viewBoxShadowPrimary: {
      shadowColor: '#000000',
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowRadius: 1,
      shadowOpacity: 0.4,
    },
    fontPrimary: {
      fontFamily: 'HelveticaNeue-Light',
    },
    fontPrimaryItalic: {
      fontFamily: 'HelveticaNeue-LightItalic',
    },
    fontSecondary: {
      fontFamily: 'HelveticaNeue-CondensedBlack',
    },
    fontRegular: {
      fontFamily: 'HelveticaNeue',
    },
    fontMedium: {
      fontFamily: 'HelveticaNeue-Medium',
    },
    fontBold: {
      fontFamily: 'HelveticaNeue-Bold',
    },
    fontBoldItalic: {
      fontFamily: 'HelveticaNeue-BoldItalic',
    },
    fontDigital: {
      fontFamily: 'Digital-7',
    },
    textColorPrimary: {
      color: '#FFFFFF',
    },
    textColorSecondary: {
      color: '#4a4a4a',
    },
    textColorTertiary: {
      color: '#FFFFFF',
    },
    textColorFour: {
      color: '#7a8fac',
      description: 'ie: VS <Username> on Match Stream cells',
    },
    textColorError: {
      color: '#bc2131',
    },
    textColorCash: {
      color: '#3ba038',
    },
    textColorZ: {
      color: '#387aa4',
    },
    textColorTicketz: {
      color: '#FD705D',
    },
    textColorLink: {
      color: '#006699',
    },
    textColorMedals: {
      color: '#317296',
    },
    textColorDepositTile: {
      color: '#FFFFFF',
    },
    textColorMatchSelectPrize: {
      color: '#FFFFFF',
    },
    textColorSideMenu: {
      color: '#FFFFFF',
    },
    textShadowOne: {
      shadowColor: '#000000',
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowRadius: 4,
      shadowOpacity: 0.4,
    },
    textShadowTwo: {
      shadowColor: '#000000',
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowRadius: 1,
      shadowOpacity: 0.3,
    },
    textShadowThree: {
      shadowColor: '#000000',
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowRadius: 1,
      shadowOpacity: 0.2,
    },
    fontSizeExtraLarge: {
      fontSize: 85,
    },
    fontSizeVeryLarge: {
      fontSize: 58,
    },
    fontSizeLarge: {
      fontSize: 42,
    },
    fontSizeMedium: {
      fontSize: 35,
    },
    fontSizeH1: {
      fontSize: 30,
    },
    fontSizeH2: {
      fontSize: 22,
    },
    fontSizeH3: {
      fontSize: 18,
    },
    fontSizeH4: {
      fontSize: 15,
    },
    fontSizeH5: {
      fontSize: 12,
    },
    fontSizeH6: {
      fontSize: 8,
    },
    btnTextColorOne: {
      color: '#ffffff',
    },
    btnTextColorTwo: {
      color: '#ffffff',
    },
    btnTextColorThree: {
      color: '#ffffff',
    },
    featuredMatchBarColor: {
      backgroundColor: '#1f8ed4',
    },
    tabHighlightColor: {
      color: '#1f8ed4',
    },
    checkMarkColor: {
      backgroundColor: '#4fa93f',
    },
    highlightTurnBasedWinColor: {
      color: '#f9eb0d',
    },
    giftBoxHighlightColor: {
      color: '#395b79',
    },
    primaryRibbonTextColor: {
      color: '#ffffff',
    },
    excitingBannerTextColor: {
      color: '#ffffff',
    },
  },
  gradients: {
    prizeViewLiveEventGradient: ['#6ac141', '#35a1a2'],
    prizeViewCashGradient: ['#6ac141', '#36923e'],
    prizeViewZGradient: ['#387aa4', '#387aa4'],
    prizeViewTicketzGradient: ['#f6604d', '#e05745'],
    bracketPrizeViewLiveEventGradient: ['#6ac141', '#35a1a2'],
    bracketPrizeViewCashGradient: ['#6ac141', '#36923e'],
    bracketPrizeViewZGradient: ['#387aa4', '#387aa4'],
    btnGradientOne: ['#FF6702', '#F89C3D'],
    btnGradientTwo: ['#2333FF', '#A00EA7'],
    btnGradientThree: ['#6861ff', '#6861ff'],
    btnGradientStore: ['#4fb031', '#29782f'],
    playButtonGradient: ['#2333FF', '#A00EA7'],
    textTicketzGradient: ['#f87861', '#e1654d'],
    textCashGradient: ['#6ac141', '#3da142'],
    textZGradient: ['#4686ad', '#2f6d98'],
    textNeutralGradient: ['#b6b6b6', '#6c6c6c'],
    overlayEntryContainerGradient: ['rgba(114,127,144,.95)', 'rgba(141,154,168,.95)'],
    backgroundViewGradient: ['#3F476B', '#6B80A4'],
    sideMenuHighlightedGradient: ['#2a6994', '#63afd6'],
    cashIconGradient: ['#E9AB24', '#FFE6C5', '#FBDE5F'],
    practiceCurrencyIconGradient: ['#5d7696', '#6da5ce'],
    profileIconGradient: ['#6ea8d2', '#5c7291'],
    progressViewDarkGradient: ['#343443', '#484858'],
    navigationBarGradient: ['#000000', '#262a38'],
    selectedCellBackgroundGradient: ['#0099c7', '#005174'],
    pointsPrizeGradient: ['#4581b1', '#5592c1', '#629dca'],
    tauntImageGradient: ['#4686ad', '#2f6d98'],
    excitingBannerGradient: ['#2e2dff', '#fc0132'],
    excitingHighlightGradient: ['#3e00cd', '#b709dd'],
    primaryRibbonGradient: ['#350c95', '#4b0cdf', '#3d03c7', '#4b0cdf', '#350c95'],
  },
  images: {
    backgroundImage: 'SKillz_Dark_Theme_BG.png',
    prizeViewZImage: null,
    prizeViewCashImage: null,
    prizeViewLiveEventImage: null,
    prizeViewTicketzImage: null,
    bracketPrizeViewZImage: null,
    bracketPrizeViewCashImage: null,
    bracketPrizeViewLiveEventImage: null,
    excitingBannerImage: null,
    interstitialBackgroundImage: null,
  },
};

export default {FallbackSkillsLightThemeProps, FallbackSkillsDarkThemeProps};
